/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useContext, useState } from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import { useListingBatch } from './useListingBatch'
import { useNavigate, useParams } from 'react-router-dom'
import { AuthContext } from '@/components/providers/SessionProvider/auth-context'
import {
  getListingBatchItemsByBatchId,
  startListingBatchProcessing
} from '@/services/listing-batches'
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import { Spinner, useToast } from '@chakra-ui/react'
import AcquireLicenseForm from './components/AcquireLicenseForm'
import { listMultipleAssetsUrls } from '@/configs/urls'
import HeaderBatchItem from '@/components/batches/HeaderBatchItem'
import {
  ASSET_LISTING_BATCH,
  ASSET_LISTING_BATCH_DESCRIPTION,
  RETURN_ASSETS_LISTING_BATCHES
} from '@/configs/strings/list-multiple-assets'
import AssetsDataLists from './components/AssetsDataLists'
import BuildLicenseListingTermsForm from './components/BuildLicenseListingTermsForm'
import { useForm } from 'react-hook-form'
import { schemaValidation } from './handlers'
import { joiResolver } from '@hookform/resolvers/joi'
import { useAuth0 } from '@auth0/auth0-react'
import FormWrapper from '@/components/forms/FormWrapper'
import { getBatchProcessInfo } from '@/utils/batches'
import { generalBatchStates } from '@/configs/server-enums'
import { getFailedDescription, getOnErrorTitle, getSuccessDescription } from '@/configs/strings/batches'
import { defaultToastConfig } from '@/configs/toast'

const ListingBatch = () => {
  const toast = useToast()
  const { processing } = generalBatchStates
  const batchType = 'Listing Batch'
  const {
    auth0UserId
  } = useContext(AuthContext)
  const navigate = useNavigate()
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const [hasAlert, setHasAlert] = useState(false)
  const [searchParams] = useState({})
  const {
    isLoading,
    setIsLoading,
    listingBatch
  } = useListingBatch({ searchParams, auth0UserId })
  const { batchId } = useParams()
  const onStartProcess = async (data) => {
    setIsLoading(true)
    try {
      const listingBatchUpdated = await startListingBatchProcessing(
        { id: listingBatch?.id, data: data },
        getAccessToken
      )

      if (listingBatchUpdated.state === processing) {
        toast({
          title: batchType,
          description: getSuccessDescription(
            batchType,
            listingBatchUpdated.name
          ),
          status: 'success',
          ...defaultToastConfig
        })
      }
    } catch (error) {
      toast({
        title: getOnErrorTitle(batchType),
        description: error.response?.data?.message || getFailedDescription(batchType),
        status: 'error',
        ...defaultToastConfig
      })
      setHasAlert(true)
    } finally {
      navigate(`/${listMultipleAssetsUrls.base}`, { replace: true })

      setIsLoading(false)
    }
  }
  const {
    batchHasErrors,
    isHiddenForm
  } = getBatchProcessInfo(listingBatch?.state)
  const {
    itemsResponse: assetResponse,
    loading,
    onChangePage,
    page,
    reloadItems,
    totalPages
  } = useFetchItemsWithParamsV2({
    fetchService: getListingBatchItemsByBatchId,
    fetchParams: { id: batchId }
  })
  const stateFromListingBatch = true
  const HeaderBatch = () => (
    <HeaderBatchItem
      title={listingBatch?.name}
      batchState={listingBatch?.state}
    />
  )
  const {
    handleSubmit,
    register,
    formState: { errors }
  } = useForm({
    resolver: joiResolver(schemaValidation)
  })

  const sendData = async () => await handleSubmit(onStartProcess)()
  return (
    <RegularTemplate
      backLabel={RETURN_ASSETS_LISTING_BATCHES}
      backUrl={`/${listMultipleAssetsUrls.base}`}
      childrenHeader={HeaderBatch}
      description={ASSET_LISTING_BATCH_DESCRIPTION}
      loadingContent={isLoading}
      title={ASSET_LISTING_BATCH}
      itemNotFound={!listingBatch}
    >
      { !isLoading &&
        <>
          { loading
            ? <div className="text-center">
                <Spinner size="lg" />
              </div>
            : listingBatch && <>
                <FormWrapper hasAlert={hasAlert}>
                  <form
                    onSubmit={handleSubmit(onStartProcess)}
                  >
                    <AcquireLicenseForm
                      hasAlert={hasAlert}
                      hasFormErrors={Object.keys(errors).length > 0}
                      isHiddenForm={isHiddenForm}
                      onStartProcess={onStartProcess}
                      sendData={sendData}
                      setHasAlert={setHasAlert}
                      stateFromListingBatch={stateFromListingBatch}
                      totalAssets={assetResponse.total}
                    />
                    { !isHiddenForm &&
                      <BuildLicenseListingTermsForm
                        register={register}
                        errors={errors}
                      />
                    }
                  </form>
                </FormWrapper>
                <AssetsDataLists
                  batchHasErrors={batchHasErrors}
                  assets={assetResponse.data}
                  batchName={listingBatch.name}
                  batchId={batchId}
                  isHiddenForm={isHiddenForm}
                  onChangePage={onChangePage}
                  page={page}
                  reloadItems={reloadItems}
                  totalPages={totalPages}
                  totalAssets={assetResponse.total}
                  onStartProcess={onStartProcess}
                />
              </>
          }
        </>
      }
    </RegularTemplate>
  )
}

export default ListingBatch
